import {
  AccessTimeFilled,
  CheckBox,
  CheckBoxOutlineBlank,
  Delete,
  Edit,
  KeyboardDoubleArrowDown,
} from "@mui/icons-material";
import { format } from "date-fns";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { LocationDto, ReminderDto, TicketDto, TicketFilterDto, UserDto } from "../../../api-client";
import {
  useDeleteReminder,
  useCreateReminder,
  useUpdateReminder,
  useCreateTicket,
  useUpdateTicket,
  useTicketFilter,
  useTicket,
} from "../../../stores/Ticketing";
import { useAllUsersBasic } from "../../../stores/User";
import useStore from "../../../helpers/useStore";
import { TicketsStoreContext } from "../../../stores/Ticketing/tickets.provider";
import { UserContext } from "../../../stores/User/User.provider";
import { attentionColors, mobiliTheme } from "../../../themes/mobiliTheme";

import { commonTabStyle } from "../../PatientOverview/tabStyles";

import { observer } from "mobx-react";
import FlatCard from "../../../atoms/FlatCard";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import WarningDialog from "../../../atoms/WarningDialog";
import { useMasterTables } from "../../../stores/MasterTables";
import notificationStore from "../../../stores/Notification/notificationStore";
import AllActivityTicket from "./AllActivityTicket";
import Involvements from "./Involvements";
import TicketComments from "./TicketComments";
import TicketHistoryTimeline from "./TicketHistoryTimeline";
import { DatePicker } from "@mui/x-date-pickers";
import EditReminderDialog from "./EditReminderDialog";
import dayjs from "dayjs";
import { TICKET_STATUS, TICKET_PRIORITY, TICKET_UI_DEFAULTS } from "../constants/ticketConstants";
import TicketIcon from './TicketIcon';

interface TicketFormProps {
  ticketId?: number;
  openTicket: boolean;
  onCloseTicketForm?: () => void;
}

const UpdateTicketForm: React.FC<TicketFormProps> = observer(
  ({ ticketId, openTicket, onCloseTicketForm }) => {
    const UserStore = useStore(UserContext);
    const { user } = UserStore;
    const { data: allUsersBasicData } = useAllUsersBasic(false);
    const allUsersBasic = useMemo(() => allUsersBasicData ?? [], [allUsersBasicData]);

    // Fetch ticket data internally using the ticketId prop
    // Cast to any for backward compatibility - TicketDto type doesn't include nested objects like location, rx, appointment
    const { data: fetchedTicket, isFetching: isTicketLoading } = useTicket(ticketId);
    const data: TicketDto = fetchedTicket as any;

    // userOptions is the React Query users list, reordered so the logged-in user
    // appears first. Derived from the fetch (no state mirror, no sync effect).
    const userOptions = useMemo(() => {
      if (!user) return [];
      const index = allUsersBasic.findIndex((u: any) => u.id === user.id);
      if (index === -1) return allUsersBasic;
      return [user, ...allUsersBasic.filter((u: any) => u.id !== user.id)];
    }, [allUsersBasic, user]);

    const [tabValue, setTabValue] = useState(0);

    // Debounced deadline state
    const [localDeadline, setLocalDeadline] = useState<Date | null>(null);
    const deadlineDebounceRef = useRef<NodeJS.Timeout | null>(null);

    // Reminders are derived directly from the fetched ticket (the React Query
    // cache), not mirrored into local state. Every reminder/ticket mutation
    // invalidates `ticketById`, so `useTicket` refetches and this list stays in
    // sync automatically - no manual re-syncing needed.
    const reminders = useMemo(
      () =>
        Array.isArray(data?.reminders)
          ? data.reminders.map((reminder: ReminderDto) => ({
              id: reminder.id,
              date: reminder.date ? new Date(reminder.date) : null,
              alternativeMessage: reminder.alternativeMessage,
              isResolved: reminder.isResolved ?? false,
              createdByUser: reminder.createdByUser as
                | {
                    id: string;
                    nickName?: string;
                    firstName?: string;
                    lastName?: string;
                  }
                | undefined,
            }))
          : [],
      [data]
    );

    const [newReminder, setNewReminder] = useState<{
      date: Date | null;
      alternativeMessage: string;
      isResolved: boolean;
    }>({
      date: null,
      alternativeMessage: "",
      isResolved: false,
    });
    const { data: masterTablesData } = useMasterTables();
    const addTicket = useCreateTicket();
    const updateTicket = useUpdateTicket();
    const { data: rawTicketFilters, isLoading: ticketFiltersLoading } = useTicketFilter();
    const ticketFilters: TicketFilterDto[] = rawTicketFilters ?? [];

    const locationsOptions = masterTablesData
      ? masterTablesData.filter((obj: any) => "location" in obj).map((obj: any) => obj.location)[0]
      : null;

    const handleTabChange = (event: any, newValue: any) => {
      setTabValue(newValue);
    };

    useEffect(() => {
      if (openTicket) {
        setUnsavedChanges(false);
      }
    }, [openTicket]);

    useEffect(() => {
      // console.log("datadkkdkdskkd", data, user);
      try {
        if (data) {
          // setTicketData(data);
          // Initialize local deadline from data
          setLocalDeadline(data.deadline ? new Date(data.deadline) : null);

          // Check if assignedToUser exists in data
          if (data.assignedToUser) {
            // Create a new object with id and label properties
            const assignedToOption = {
              id: data.assignedToUser.id,
              label: `${data.assignedToUser.firstName} ${data.assignedToUser.lastName}`,
            };
            setSelectedAssigneeOption(data.assignedToUser); // Update selectedOption with assignedToUser
          }
          if (data.createdByUser?.id) {
            // Create a new object with id and label properties
            const createOption = {
              id: data.createdByUser.id,
              label: `${data.createdByUser.firstName} ${data.createdByUser.lastName}`,
            };
            setTicketData({
              ...data,
              createdByUser: {
                id: data?.createdByUser.id as any,
                nickName: data?.createdByUser.nickName,
                firstName: data?.createdByUser.firstName,
                lastName: data?.createdByUser.lastName,
              },
              locationId: data?.location?.id || user?.locationId!,
            });
          } else if (!data.createdByUser?.id) {
            setTicketData({
              ...data,
              createdByUser: {
                id: user?.id as any,
                nickName: user?.nickName,
                firstName: user?.firstName,
                lastName: user?.lastName,
              },
              locationId: data?.location?.id || user?.locationId!,
            });
          }
        } else {
          setTicketData((prevTicketData) => ({
            ...prevTicketData,
            createdByUser: {
              id: user?.id as any,
              nickName: user?.nickName,
              firstName: user?.firstName,
              lastName: user?.lastName,
            },
            locationId: user?.locationId!,
          }));
        }
      } catch (error) {
        console.error("Error while fetching data:", error);
      }
    }, [data, user]);

    const initialValues = {
      assignedToUser: { id: "" },
      description: "",
      activities: [],
      createdByUser: user
        ? {
            id: user?.id as any,
            nickName: user?.nickName,
            firstName: user?.firstName,
            lastName: user?.lastName,
          }
        : undefined,
      updatedByUser: user
        ? {
            id: user?.id as any,
            nickName: user?.nickName,
            firstName: user?.firstName,
            lastName: user?.lastName,
          }
        : undefined,
      ticketType: "",
      ticketStatus: TICKET_UI_DEFAULTS.STATUS,
      priority: TICKET_PRIORITY.LOW,
      comments: "",
      rxId: data?.rx?.id ,
      patientId: data?.patient?.id || data?.patientId,
      appointmentId: data?.appointment?.id || data?.appointmentId,
      locationId: user?.locationId || "",
    };
    const [ticketData, setTicketData] = useState<TicketDto>(initialValues);

    const [unsavedChangesWarning, setShowunsavedChangesWarning] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false); // Flag to track unsaved changes
    // MD-334: edits made in the "Betroffen" linkage editor but not saved there.
    // Linkages save through their own mutation, not handleSubmit, so they need
    // their own handle - otherwise closing drops them without a word.
    const [pendingLinkages, setPendingLinkages] = useState<{ save: () => Promise<void> } | null>(
      null
    );
    const [deleteReminderDialog, setDeleteReminderDialog] = useState<{
      open: boolean;
      index: number | null;
    }>({
      open: false,
      index: null,
    });

    const [editReminderDialog, setEditReminderDialog] = useState<{
      open: boolean;
      reminder: {
        id?: number;
        date: Date | null;
        alternativeMessage?: string;
        isResolved: boolean;
      } | null;
      index: number | null;
    }>({
      open: false,
      reminder: null,
      index: null,
    });

    const handleChange = (e: any) => {
      setUnsavedChanges(true);

      const { name, value } = e.target;
      const tktData = { ...ticketData, [name]: value };
      setTicketData(tktData);
    };
    const isValidTicketStatus = (
      tktFilters: TicketFilterDto[],
      ticketType: string | undefined,
      ticketStatus: string | undefined
    ): boolean => {
      if (!ticketType) return false;

      return (
        tktFilters
          .find((filter) => String(filter.ticketType.value) === String(ticketType)) // Ensure filter is found
          ?.statuses?.some((statusObj) => String(statusObj.value) === String(ticketStatus)) || false
      );
    };

    const isValidTicketPriority = (
      tktFilters: TicketFilterDto[],
      ticketType: string | undefined,
      ticketPriority: string | undefined
    ): boolean => {
      if (!ticketType) return false;

      return (
        tktFilters
          .find((filter) => String(filter.ticketType.value) === String(ticketType)) // Ensure filter is found
          ?.priorities?.some((statusObj) => String(statusObj.value) === String(ticketPriority)) ||
        false
      );
    };

    const validateTicketData = (ticketData: TicketDto): string => {
      let errorMessage = "";

      if (!ticketData.ticketType) return (errorMessage = "Problemtyp fehlt");
      if (!ticketData.ticketStatus) return (errorMessage = "Status fehlt");

      if (!isValidTicketStatus(ticketFilters, ticketData.ticketType, ticketData.ticketStatus))
        return (errorMessage = "ungültiger Status");

      // Only validate priority if this ticket type requires priorities
      const ticketTypeFilter = ticketFilters.find(
        (filter) => filter.ticketType.value === ticketData.ticketType
      );
      if (
        ticketTypeFilter?.hasPriorities &&
        !isValidTicketPriority(ticketFilters, ticketData.ticketType, ticketData.priority)
      )
        return (errorMessage = "ungültige Priorität");

      return errorMessage;
    };
    const handleSubmit = async (e?: any) => {
      try {
        if (e) {
          e.preventDefault();
        }

        const ticketPayload: TicketDto = {
          ...ticketData,
          priority: ticketData.priority?.toUpperCase(), // Ensure priority is uppercase
          reminders: undefined, // Exclude reminders from the update payload (this is done on purpose bc they are handled separately)
        };

        //Validate ticketData
        const errorMessage = validateTicketData(ticketPayload);
        if (errorMessage) return notificationStore.showMessage(errorMessage, "error");

        ticketData.updatedByUser = {
          id: user?.id as any,
          nickName: user?.nickName,
          firstName: user?.firstName,
          lastName: user?.lastName,
        };
        //If status is not present then select the first status

        // Set default ticketType and ticketStatus if not present

        if (!ticketData.ticketType) {
          // console.log("ajkskks", ticketFilters[0], ticketFilters);
          let firstTicketFilter = ticketFilters[0];
          ticketData.ticketType = firstTicketFilter.ticketType.value;
          ticketData.ticketStatus = firstTicketFilter.statuses[0].value;
          ticketData.priority = firstTicketFilter.priorities[0].value;
        }

        // Validate and update ticketStatus
        const validStatus = isValidTicketStatus(
          ticketFilters,
          ticketData.ticketType,
          ticketData.ticketStatus
        );

        if (!validStatus) {
          ticketData.ticketStatus = ticketFilters.find(
            (filter) => filter.ticketType.value === ticketData.ticketType
          )?.statuses[0].value;
        }

        //Validate and update ticket prority
        const validPriority = isValidTicketPriority(
          ticketFilters,
          ticketData.ticketType,
          ticketData.priority
        );

        if (!validPriority) {
          ticketData.priority = ticketFilters.find(
            (filter) => filter.ticketType.value === ticketData.ticketType
          )?.priorities[0].value;
        }

        // Validate empty ticket locationd
        if (!ticketData.locationId) {
          return notificationStore.showMessage("Fehlender Standort", "error");
        }
        let response;
        if (!ticketData.id) {
          addTicket.mutate(ticketPayload);
        } else {
          updateTicket.mutate({ ticketId: ticketData.id as number, ticketData: ticketPayload });

          // if (onTicketUpdate && ticketData.id) {
          //   onTicketUpdate(response);
          // }
        }

        setUnsavedChanges(false);
        handleClose();
        // if (setRefresh) {
        //   // setRefresh(!refresh);
        //   setRefresh(true);
        // }

        setShowunsavedChangesWarning(false);

        // if (response) {
        //   setTicketData(response);
        // }
      } catch (error) {
        console.error("Error while ticket creation:", error);
      }
    };

    const handleAssignToMe = (type: string) => {
      setUnsavedChanges(true);
      // Assign the logged-in user
      if (user) {
        if (type === "assignedToUser") {
          setSelectedAssigneeOption(user); // Update the selected option state with the logged-in user's data
          setTicketData({
            ...ticketData,
            assignedToUser: {
              id: user.id as any,
              nickName: user?.nickName,
              firstName: user?.firstName,
              lastName: user?.lastName,
            },
          }); // Assign the logged-in user's ID
        }
        if (type === "createdByUser") {
          setTicketData({
            ...ticketData,
            createdByUser: {
              id: user.id as any,
              nickName: user?.nickName,
              firstName: user?.firstName,
              lastName: user?.lastName,
            },
          }); // Assign the logged-in user's ID
        }
      }
      // console.log(
        // "Assign to me clicked",
// 
        // ticketData.assignedToUser?.id,
        // user
      // );
    };
    const [fetchingMoreUsers, setFetchingMoreUsers] = useState(false);
    // const handleMoreUsersClick = async () => {
    //   setFetchingMoreUsers(true);
    //   try {
    //     const moreUsers = await getAllUsers();
    //     // Filter out duplicate users by ID
    //     const filteredUsers = moreUsers.filter(
    //       (user) => !userOptions.some((existingUser: any) => existingUser.id === user.id)
    //     );
    //     setUserOptions((prevUsers: any) => [...prevUsers, ...filteredUsers]);
    //     // setFetchingMoreUsers(false);
    //   } catch (error) {
    //     console.error("Error fetching more users:", error);
    //     setFetchingMoreUsers(false);
    //   }
    // };
    // console.log(
      // "selectedOption",
// 
      // ticketData.assignedToUser?.id,
      // data
    // );
    const [anchorEl1, setAnchorEl1] = useState(null);
    const [selectedPriority, setSelectedPriority] = useState(
      <KeyboardDoubleArrowDown sx={{ color: attentionColors.lowPriority }} />
    ); // Default selected priority symbol
    const handleClick = (event: any) => {
      setAnchorEl1(event.currentTarget);
    };

    const handleClose = () => {
      if (onCloseTicketForm) {
        setTicketData(initialValues);
        setUnsavedChanges(false); // Reset unsavedChanges when closing
        setPendingLinkages(null);
        // setTicketData(getInitialTicketData());
        // setTabValue(0); // Reset tab to default (Alle) when closing
        onCloseTicketForm();
      }
    };
    const handleCloseTicketFormDialog = () => {
      if (unsavedChanges || pendingLinkages) {
        // If there are unsaved changes, set a state to open the warning dialog
        setShowunsavedChangesWarning(true);
      } else {
        handleClose();
      }
    };

    const handleUnsavedChangesWarning = async () => {
      // Linkages are persisted by their own mutation, so save them before
      // handing over to handleSubmit - which knows nothing about them.
      if (pendingLinkages) {
        await pendingLinkages.save();
        setPendingLinkages(null);
      }
      if (unsavedChanges) {
        handleSubmit();
      } else {
        // Only the linkage edit was outstanding; it is saved now.
        setShowunsavedChangesWarning(false);
        handleClose();
      }
    };

    const handleCloseUnsavedChangesWarning = () => {
      // setTabValue(0); // Reset tab to default when closing without saving
      handleClose();
      // If user cancels and chooses not to continue, close the warning dialog
      setShowunsavedChangesWarning(false);
    };

    const createReminderMutation = useCreateReminder();
    const updateReminderMutation = useUpdateReminder();

    const handleToggleReminderStatus = async (reminderId: number) => {
      const reminder = reminders.find((r) => r.id === reminderId);
      if (!reminder || !ticketData.id) return;

      try {
        await updateReminderMutation.mutateAsync({
          ticketId: ticketData.id,
          reminderId: reminderId,
          date: reminder.date ? dayjs(reminder.date).format("YYYY-MM-DD") : undefined,
          alternativeMessage: reminder.alternativeMessage,
          isResolved: !reminder.isResolved,
        });
        // Mutation invalidates `ticketById`; the derived reminders list refreshes.
      } catch (error) {
        console.error("Error toggling reminder status:", error);
      }
    };

    const handleAddReminder = async () => {
      if (!newReminder.date || !ticketData.id || !user?.id) return;

      try {
        await createReminderMutation.mutateAsync({
          ticketId: ticketData.id,
          date: newReminder.date,
          alternativeMessage: newReminder.alternativeMessage || "",
        });
        // Mutation invalidates `ticketById`; the derived reminders list refreshes.

        // Auto-progress status if currently NEW
        if (ticketData.ticketStatus === TICKET_STATUS.NEW) {
          const availableStatuses =
            ticketFilters?.find((filter) => filter.ticketType.value === ticketData.ticketType)
              ?.statuses || [];
          const inProgressStatus = availableStatuses.find(
            (status) => status.value === TICKET_STATUS.IN_PROGRESS
          );

          if (inProgressStatus && setTicketData) {
            setTicketData((prev) => ({
              ...prev,
              ticketStatus: inProgressStatus.value,
            }));
          }
        }

        // Clear the form for the next reminder
        setNewReminder({
          date: null,
          alternativeMessage: "",
          isResolved: false,
        });
      } catch (error) {
        console.error("Error adding reminder:", error);
      }
    };

    const deleteReminderMutation = useDeleteReminder();

    const handleDeleteReminder = async () => {
      if (deleteReminderDialog.index !== null && ticketData.id) {
        const reminder = reminders[deleteReminderDialog.index];
        if (reminder.id) {
          try {
            await deleteReminderMutation.mutateAsync({
              ticketId: ticketData.id,
              reminderId: reminder.id,
            });
            // Mutation invalidates `ticketById`; the derived reminders list refreshes.
            setUnsavedChanges(true);
          } catch (error) {
            console.error("Error deleting reminder:", error);
          }
        } else {
          // Reminder was never persisted (no id) - nothing to delete server-side.
          setUnsavedChanges(true);
        }
        setDeleteReminderDialog({ open: false, index: null });
      }
    };

    const handlePrioritySelect = (prioritySymbol: any, value: string) => {
      setSelectedPriority(prioritySymbol); // Update selected priority symbol
      setTicketData({ ...ticketData, priority: value }); // Update ticket data with selected priority
    };

    // Assignee Popover
    const [selectedAssigneeOption, setSelectedAssigneeOption] = useState(
      ticketData?.assignedToUser?.id ? ticketData.assignedToUser : null
    );
    const [openAssignee, setOpenAssignee] = useState(false);

    const handleAssigneeClose = () => {
      setOpenAssignee(false); // Close dropdown manually when needed
    };

    const handleAssigneeSelect = (event: any, value: any) => {
      handleAssigneeClose(); // Close dropdown after selection
      if (value?.id === "self-assign") {
        handleAssignToMe("assignedToUser"); // Assign to self if chosen
      } else if (value) {
        setSelectedAssigneeOption(value);
        setTicketData({
          ...ticketData,
          assignedToUser: {
            id: value.id,
            nickName: value.nickName,
            firstName: value.firstName,
            lastName: value.lastName,
          },
        });
      }
    };

    const filterCurrentLocation = (
      locationId: string,
      locations: LocationDto[]
    ): LocationDto | null => {
      const locationObj: LocationDto | undefined = locations?.filter(
        (location: LocationDto) => location.id === locationId
      )?.[0];

      if (locationObj) {
        // return { label: locationObj.name, value: locationObj.id };
        return locationObj;
      }
      return null;
    };
    const getLocationOptionObj = (locationId: string) => {
      const locationObj = filterCurrentLocation(locationId, locationsOptions);
      // console.log("LOCATION_OPTIONS", locationId, locationObj);
      if (locationObj) {
        return { label: locationObj.name, value: locationObj.id };
      }
      return null;
    };

    // Creater Popover
    // ...existing code...

    // Helper for instant API update
    const updateTicketField = async (fieldName: string, value: any,extraFields?: Partial<TicketDto>) => {
      if (!ticketData.id) return;
      try {
        const payload = {
          ...ticketData,
          [fieldName]: value,
             ...extraFields,
          updatedByUser: { id: user?.id },
        };

        // Handle assignedToUser field updates
        if (fieldName === 'assignedToUser') {
          if (value === null) {
            // Clear assignment
            payload.assignedToUserId = "";
          } else if (value && value.id) {
            // Assign to user
            payload.assignedToUserId = value.id;
          }
        }

        await updateTicket.mutateAsync({ ticketId: ticketData.id, ticketData: payload });
        setTicketData((prev: TicketDto) => ({ ...prev, [fieldName]: value }));

        // The backend automatically creates a follow-up reminder ("Wiedervorlage",
        // 7 days out) when an Rx order ticket moves into "Bestellt". The mutation
        // invalidates `ticketById`, so `useTicket` refetches and the derived
        // `reminders` list picks up that new reminder automatically.

        // Update the assignee state when the assignedToUser field changes
        if (fieldName === 'assignedToUser') {
          setSelectedAssigneeOption(value);
        }

      } catch (error) {
        notificationStore.showMessage("Fehler beim Aktualisieren des Tickets", "error");
      }
    };

    // Persist a set of linked-entity changes (patient / Rx / doctor / clinic) in a
    // single update. The refreshed ticket (with nested patient/doctor/clinic) is
    // synced back into local state by the data-sync effect above.
    const handleLinkagesChange = async (changes: Partial<TicketDto>) => {
      if (!ticketData.id || !changes || Object.keys(changes).length === 0) return;
      try {
        const payload = {
          ...ticketData,
          ...changes,
          updatedByUser: { id: user?.id },
        };
        await updateTicket.mutateAsync({ ticketId: ticketData.id, ticketData: payload });
        setTicketData((prev: TicketDto) => ({ ...prev, ...changes }));
      } catch (error) {
        notificationStore.showMessage("Fehler beim Aktualisieren des Tickets", "error");
      }
    };

    // Debounced deadline update - only triggers API when date is valid and complete
    const debouncedDeadlineUpdate = useCallback(
      async (newDate: Date | null) => {
        if (!ticketData.id) return;

        // Clear any existing debounce timer
        if (deadlineDebounceRef.current) {
          clearTimeout(deadlineDebounceRef.current);
        }

        // Set up debounced API call
        deadlineDebounceRef.current = setTimeout(async () => {
          try {
            // Update deadline
            await updateTicketField("deadline", newDate ? newDate.toISOString() : undefined,newDate && ticketData.ticketStatus === TICKET_STATUS.NEW ? { ticketStatus: TICKET_STATUS.IN_PROGRESS } : undefined);

            // Auto-progress status from NEW to IN_PROGRESS when setting deadline
            // if (newDate && ticketData.ticketStatus === TICKET_STATUS.NEW) {
            //   await updateTicketField("ticketStatus", TICKET_STATUS.IN_PROGRESS);
            // }
          } catch (error) {
            console.error("Error updating deadline:", error);
          }
        }, 500); // 500ms debounce
      },
      [ticketData.id, ticketData.ticketStatus]
    );

    // Cleanup debounce timer on unmount
    useEffect(() => {
      return () => {
        if (deadlineDebounceRef.current) {
          clearTimeout(deadlineDebounceRef.current);
        }
      };
    }, []);

    // Helper to validate if a date is complete and valid
    const isValidCompleteDate = (date: Date | null): boolean => {
      if (!date) return true; // null is valid (clearing the date)
      // Check if it's a valid Date object and not NaN
      return date instanceof Date && !isNaN(date.getTime()) && date.getFullYear() > 1900;
    };

    return (
      <Dialog open={openTicket} fullWidth maxWidth="xl" >
        <DialogTitle>
          <TicketIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          {/* {ticketData.id ? `Ticket: ${ticketData.id}` : "Neu Ticket"} */}
          {`Ticket: ${ticketData.id}`}
        </DialogTitle>

        <DialogContent>
          {/* Loading indicator - shows when any data is being fetched or mutations are pending */}
          {(isTicketLoading || updateTicket.isPending || createReminderMutation.isPending || updateReminderMutation.isPending || deleteReminderMutation.isPending) && (
            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
          )}
          <Box
            sx={{
              display: "flex",
              width: "98%",
              paddingTop: 1,
              margin: "0 auto",
              justifyContent: "space-between",
            }}
          >
              <Box sx={{ flex: "0 0 58%" }}>
                <Grid container sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Grid
                    container
                    spacing={2}
                    sx={{ display: "flex", flexDirection: "column", mt: "-1rem", height: 65 }}
                  >
                    <Grid item xs={12} sx={{ border: "0px solid red", mb: 0 }}>
                      <Autocomplete
                        sx={{ margin: "0px", width: "100%" }}
                        disablePortal
                        options={
                          ticketFilters?.map((filter) => ({
                            value: filter.ticketType.value,
                            label: filter.ticketType.label,
                          })) || []
                        }
                        value={
                          ticketFilters?.find(
                            (filter) => filter.ticketType.value === ticketData.ticketType
                          )?.ticketType || null
                        }
                        onChange={async (e, value) => {
                          await updateTicketField("ticketType", value?.value || "");
                        }}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Problemtyp"
                            name="ticketType"
                            sx={{ margin: "0px", width: "100%" }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 0 }}>
                      <Autocomplete
                        sx={{ margin: "0px", width: "100%" }}
                        disablePortal
                        options={
                          locationsOptions?.map((location: any) => ({
                            value: location.id,
                            label: location.name,
                          })) || []
                        }
                        value={
                          ticketData.locationId ? getLocationOptionObj(ticketData.locationId) : null
                        }
                        onChange={async (e, value) => {
                          await updateTicketField("locationId", value?.value || "");
                        }}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Standort"
                            name="location"
                            sx={{ margin: "0px", width: "100%" }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ display: "flex", mt: "-0.25rem" }}>
                    <Grid
                      item
                      xs={2}
                      sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}
                    >
                      <Typography sx={{ textAlign: "center" }}>Erstellt von</Typography>

                      {ticketData.createdByUser ? (
                        <MobiliAvatar
                          user={ticketData.createdByUser}
                          sx={{ width: 32, height: 32, fontSize: "0.8rem", marginTop: "0.6rem" }}
                        />
                      ) : (
                        <MobiliAvatar
                          user={user || undefined}
                          sx={{ width: 32, height: 32, fontSize: "0.8rem", marginTop: "0.6rem" }}
                        />
                      )}
                    </Grid>

                    <Grid
                      item
                      xs={3}
                      sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}
                    >
                      <Typography sx={{ textAlign: "center" }}>Erledigt bis</Typography>

                      <div style={{ display: "flex" }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: "transparent",
                            color: "text.primary",
                            marginLeft: "1.75rem",
                            marginTop: "0.6rem",
                          }}
                        >
                          <AccessTimeFilled fontSize="small" />
                        </Avatar>
                        <DatePicker
                          label={false}
                          value={localDeadline}
                          onChange={(newDate: Date | null) => {
                            // Always update local state immediately for responsive UI
                            setLocalDeadline(newDate);

                            // Only trigger API call if the date is valid and complete (or null for clearing)
                            if (isValidCompleteDate(newDate)) {
                              debouncedDeadlineUpdate(newDate);
                            }
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: { width: 150 },
                            },
                          }}
                        />
                      </div>
                    </Grid>

                    <Grid
                      item
                      xs={5.75}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        flexDirection: "column",
                        marginLeft: "auto",
                      }}
                    >
                      <Autocomplete
                        disablePortal
                        options={[{ label: "Assign to me", id: "self-assign" }, ...userOptions]}
                        value={selectedAssigneeOption}
                        onChange={async (
                          event,
                          value: UserDto | { label: string; id: string } | null
                        ) => {
                          if (value && value.id === "self-assign") {
                            // Handle self-assignment
                            const userObj = {
                              id: user?.id,
                              nickName: user?.nickName,
                              firstName: user?.firstName,
                              lastName: user?.lastName,
                            };
                            setSelectedAssigneeOption(user);
                            await updateTicketField("assignedToUser", userObj);
                          } else {
                            // Handle regular assignment or unassignment
                            const selectedUser = value && "firstName" in value ? value : null;
                            setSelectedAssigneeOption(selectedUser);
                            await updateTicketField(
                              "assignedToUser",
                              selectedUser
                                ? {
                                    id: selectedUser.id,
                                    nickName: selectedUser.nickName,
                                    firstName: selectedUser.firstName,
                                    lastName: selectedUser.lastName,
                                  }
                                : null
                            );
                          }
                        }}
                        getOptionLabel={(option: any) =>
                          option.id === "self-assign"
                            ? option.label
                            : `${option.firstName} ${option.lastName}`
                        }
                        renderOption={(props, option: any) => (
                          <li
                            {...props}
                            style={{ width: "100%", display: "flex", alignItems: "center" }}
                            key={option.id}
                          >
                            {option.id === "self-assign" ? (
                              <Typography variant="body1" fontSize="14px" padding="2px">
                                Assign to me
                              </Typography>
                            ) : (
                              <>
                                <Avatar
                                  sx={{ width: 32, height: 32, fontSize: "0.8rem", marginRight: 1 }}
                                >
                                  {option.nickName}
                                </Avatar>
                                <Typography variant="body1" fontSize="12px" padding="2px">
                                  {option.firstName} {option.lastName}
                                </Typography>
                              </>
                            )}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Zugewiesen"
                            variant="outlined"
                            sx={{ width: "100%", marginTop: 1 }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <Tooltip
                                  title={
                                    selectedAssigneeOption || (ticketData?.assignedToUser as any)
                                  }
                                >
                                  <>
                                    <MobiliAvatar
                                      user={selectedAssigneeOption || ticketData?.assignedToUser}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: "0.8rem",
                                        cursor: "pointer",
                                        display: "flex",
                                      }}
                                    />
                                    {params.InputProps.endAdornment}
                                  </>
                                </Tooltip>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    spacing={2}
                    sx={{ display: "flex", justifyContent: "space-between", mt: "-1.5rem" }}
                  >
                    <Grid
                      item
                      xs={5.75}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      <Involvements ticketData={ticketData} editable onLinkagesChange={handleLinkagesChange} onNavigate={handleClose} onPendingLinkagesChange={setPendingLinkages} />
                    </Grid>

                    <Grid
                      item
                      xs={3.2}
                      md={3.2}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        flexDirection: "column",
                        border: "0px solid red",
                        marginRight: 1.5,
                      }}
                    >
                      {ticketData.ticketType && (
                        <FlatCard title="Status" sx={{ flexGrow: 1 }}>
                          <RadioGroup
                            sx={{ display: "flex", padding: "0px", margin: "0px" }}
                            aria-label="status"
                            name="status"
                            value={ticketData?.ticketStatus || TICKET_UI_DEFAULTS.STATUS}
                            onChange={async (e) => {
                              await updateTicketField("ticketStatus", e.target.value);
                            }}
                          >
                            {ticketFilters
                              .find((filter) => filter.ticketType.value == ticketData?.ticketType)
                              ?.statuses.map((status, index) => (
                                <FormControlLabel
                                  key={status.value + index}
                                  value={status.value}
                                  control={<Radio sx={{ padding: "2px" }} />}
                                  label={
                                    <Typography sx={{ fontSize: "0.75rem" }}>{status.label}</Typography>
                                  }
                                />
                              ))}
                          </RadioGroup>
                        </FlatCard>
                      )}
                    </Grid>

                    <Grid
                      item
                      xs={2.5}
                      md={2.5}
                      sx={{ border: "0px solid red", flexDirection: "column" }}
                    >
                      {ticketData.ticketType &&
                        ticketFilters.find(
                          (filter) => filter.ticketType.value === ticketData?.ticketType
                        )?.hasPriorities && (
                          <FlatCard title="Priorität" sx={{ flexGrow: 1 }}>
                            <RadioGroup
                              sx={{ display: "flex", padding: "0px", margin: "0px" }}
                              aria-label="priority"
                              name="priority"
                              value={ticketData?.priority || TICKET_PRIORITY.LOW}
                              onChange={async (e) => {
                                await updateTicketField("priority", e.target.value);
                              }}
                            >
                              {ticketFilters
                                .find((filter) => filter.ticketType.value == ticketData?.ticketType)
                                ?.priorities.map((priority, index) => (
                                  <FormControlLabel
                                    key={priority.value + index}
                                    value={priority.value}
                                    control={<Radio sx={{ padding: "2px" }} />}
                                    label={
                                      <Typography sx={{ fontSize: "0.75rem" }}>{priority.label}</Typography>
                                    }
                                  />
                                ))}
                            </RadioGroup>
                          </FlatCard>
                        )}
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{ border: "0px solid red", height: "80px", marginTop: "0" }}
                  >
                    <TextField
                      fullWidth
                      label="Beschreibung"
                      name="description"
                      value={ticketData.description}
                      onChange={(e) => {
                        setUnsavedChanges(true);
                        setTicketData((prev) => ({ ...prev, description: e.target.value }));
                      }}
                      variant="outlined"
                      multiline
                      rows={2}
                      sx={{ height: "100%" }}
                    />
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 0.5 }}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                          setTicketData((prev) => ({
                            ...prev,
                            description: data?.description || "",
                          }));
                          setUnsavedChanges(false);
                        }}
                        disabled={!unsavedChanges}
                      >
                        Abbrechen
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                          if (!ticketData.id) return;
                          try {
                            await updateTicketField("description", ticketData.description);
                            setUnsavedChanges(false);
                            notificationStore.showMessage("Beschreibung gespeichert", "success");
                          } catch (error) {
                            notificationStore.showMessage(
                              "Fehler beim Speichern der Beschreibung",
                              "error"
                            );
                          }
                        }}
                        disabled={!unsavedChanges || updateTicket.isPending}
                      >
                        Speichern
                      </Button>
                    </Box>
                  </Grid>

                  <Typography
                    sx={{
                      textAlign: "center",
                      marginTop: "0.5rem",
                      paddingLeft: "0.5rem",
                      fontSize: 20,
                    }}
                  >
                    Wiedervorlage
                  </Typography>

                  <Box
                    sx={{
                      backgroundColor: mobiliTheme.lowLevelContainerBackground,
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      padding: "1rem",
                      pt: 0.2,
                      marginTop: "0.5rem",
                      width: "100%",
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: "0", mt: 0, fontWeight: "bold" }}>
                      Wiedervorlage hinzufügen
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 0 }}>
                      <Grid item xs={3.5}>
                        <div style={{ display: "flex" }}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: "transparent",
                              color: "text.primary",
                              marginTop: "0.6rem",
                            }}
                          >
                            <AccessTimeFilled fontSize="small" />
                          </Avatar>
                          <DatePicker
                            label={false}
                            value={newReminder.date}
                            minDate={new Date()}
                            onChange={(newDate: Date | null) => {
                              setNewReminder((prev) => ({
                                ...prev,
                                date: newDate,
                              }));
                            }}
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: { width: 150 },
                              },
                              day: {
                                sx: {
                                  "&.MuiPickersDay-today": {
                                    border: "1px solid",
                                  },
                                  "&.Mui-disabled": {
                                    color: "text.disabled",
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={8.5}>
                        <Box sx={{ width: "100%", position: "relative", mt: "-1rem" }}>
                          <TextField
                            fullWidth
                            label="Alternative Botschaften"
                            name="alternativeMessage"
                            value={newReminder.alternativeMessage}
                            onChange={(e) =>
                              setNewReminder((prev) => ({
                                ...prev,
                                alternativeMessage: e.target.value,
                              }))
                            }
                            variant="outlined"
                            multiline
                            rows={2}
                          />
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "flex-end",
                              mt: 0.2,
                            }}
                          >
                            <Button
                              variant="contained"
                              onClick={handleAddReminder}
                              disabled={!newReminder.date || createReminderMutation.isPending}
                            >
                              {createReminderMutation.isPending ? "Speichern..." : "Wiedervorlage hinzufügen"}
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Display existing reminders */}
                    {reminders.length > 0 && (
                      <Box sx={{ mt: -2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
                          Bestehende Wiedervorlage
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "white",
                            border: "2px solid #e0e0e0",
                            borderRadius: "6px",
                            overflow: "hidden",
                            pt: 1,
                            pb: 2,
                          }}
                        >
                          {reminders.map((reminder, index) => {
                            const displayDate = reminder.date
                              ? format(new Date(reminder.date), "dd.MM.yyyy")
                              : "-";

                            const statusIcon = (() => {
                              if (reminder.isResolved) {
                                return <CheckBox sx={{ fontSize: 18 }} />;
                              }

                              const isExpired =
                                reminder.date && new Date() > new Date(reminder.date);
                              if (isExpired) {
                                return (
                                  <Box
                                    component="span"
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      backgroundColor: "error.main",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: 0.5,
                                      color: "white",
                                      fontWeight: "bold",
                                      fontSize: "14px",
                                    }}
                                  >
                                    !
                                  </Box>
                                );
                              }

                              return <CheckBoxOutlineBlank sx={{ fontSize: 18 }} />;
                            })();

                            return (
                              <Box
                                key={reminder.id ?? index}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  py: 0.25,
                                  position: "relative",
                                  "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: 0,
                                    left: "2rem",
                                    right: "2rem",
                                    height: "2px",
                                    backgroundColor: "rgba(0, 0, 0, 0.15)",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    minWidth: "180px",
                                    paddingLeft: "2rem",
                                  }}
                                >
                                  <Typography variant="body2">{displayDate}</Typography>
                                </Box>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    flex: 1,
                                    color: "text.primary",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {reminder.alternativeMessage || "-"}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                  {reminder.createdByUser && (
                                    <MobiliAvatar
                                      user={reminder.createdByUser}
                                      sx={{ width: 24, height: 24, fontSize: "0.7rem" }}
                                    />
                                  )}

                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditReminderDialog({
                                        open: true,
                                        reminder: {
                                          id: reminder.id,
                                          date: reminder.date,
                                          alternativeMessage: reminder.alternativeMessage,
                                          isResolved: reminder.isResolved,
                                        },
                                        index: index,
                                      });
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setDeleteReminderDialog({ open: true, index });
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                  <Box
                                    onClick={() =>
                                      reminder.id && handleToggleReminderStatus(reminder.id)
                                    }
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: 1,
                                      px: 0,
                                      py: 0.5,
                                      minWidth: 44,
                                      marginRight: "1.5rem",
                                      cursor: "pointer",
                                      "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                      },
                                    }}
                                  >
                                    {statusIcon}
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Box>

              {/* Right section - Tabs */}
              <Box
                sx={{
                  flex: "0 0 40%",
                  position: "relative",
                  maxWidth: "40%",
                  overflow: "hidden",
                }}
              >
                <Paper
                  sx={{
                    backgroundColor: mobiliTheme.lowLevelContainerBackground,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#D9DFE2",
                      // flexGrow: 1,
                      height: "40px",
                      paddingLeft: 2,
                      gap: 2,
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: "bold", color: "black" }}>
                      History:
                    </Typography>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      indicatorColor="primary"
                      textColor="inherit"
                      centered
                      sx={{
                        "& .MuiTab-root": {
                          color: "black",
                        },
                        "& .MuiTab-root.Mui-selected": {
                          color: "black",
                        },
                      }}
                    >
                      <Tab iconPosition="start" label="Kommentare" value={0} sx={commonTabStyle} />
                      <Tab iconPosition="start" label="Aktivität" value={1} sx={commonTabStyle} />
                      <Tab iconPosition="start" label="Alle" value={2} sx={commonTabStyle} />
                    </Tabs>
                  </Box>
                  <Box sx={{ flexGrow: 1, overflow: "auto", p: 2, backgroundColor: "white" }}>
                    {unsavedChangesWarning && (
                      <WarningDialog
                        open={true}
                        onClose={handleCloseUnsavedChangesWarning}
                        onContinue={handleUnsavedChangesWarning}
                        title="Änderungen speichern?"
                        content="Du hast einige Änderungen gemacht. Möchtest du diese speichern, bevor du schließt?"
                        isConfirmSave
                      />
                    )}
                    {tabValue === 0 && (
                      <TicketComments
                        selectedTicket={ticketData}
                        setTicketData={(ticketData: TicketDto) => setTicketData(ticketData)}
                      />
                    )}
                    {tabValue === 1 && (
                      <TicketHistoryTimeline
                        selectedTicket={ticketData}
                        setTicketData={setTicketData}
                      />
                    )}
                    {tabValue === 2 && ticketData.id && (
                      <AllActivityTicket selectedTicketId={ticketData.id} />
                    )}
                  </Box>
                </Paper>
              </Box>
            </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTicketFormDialog} variant="text">
            Schließen
          </Button>
        </DialogActions>

        {/* Delete Reminder Confirmation Dialog */}
        <Dialog
          open={deleteReminderDialog.open}
          onClose={() => setDeleteReminderDialog({ open: false, index: null })}
        >
          <DialogTitle>Wiedervorlage löschen</DialogTitle>
          <DialogContent>
            <Typography>Möchten Sie diese Wiedervorlage wirklich löschen?</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteReminderDialog({ open: false, index: null })}
              variant="text"
            >
              Abbrechen
            </Button>
            <Button onClick={handleDeleteReminder} variant="contained" color="error">
              Löschen
            </Button>
          </DialogActions>
        </Dialog>

        <EditReminderDialog
          ticketId={ticketData.id!}
          open={editReminderDialog.open}
          onClose={() => setEditReminderDialog((prev) => ({ ...prev, open: false }))}
          reminder={
            editReminderDialog.reminder || {
              date: null,
              alternativeMessage: "",
              isResolved: false,
            }
          }
          // The dialog's useUpdateReminder mutation invalidates `ticketById`, so the
          // derived reminders list refreshes on its own - no onSave sync required.
        />
      </Dialog>
    );
  }
);

export default UpdateTicketForm;
